import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { RequireRole } from '@/features/auth/RequireRole'
import { Spinner } from '@/components/ui/Spinner'
import { RouteErrorBoundary } from '@/components/ErrorBoundary'
import { ROUTES } from '@/config/routes'
import { useRole } from '@/hooks/useRole'
import { useAuth } from '@/hooks/useAuth'
import { getDefaultRouteForRole } from '@/utils/roleGuard'

/** Redirects authenticated users to their role-based home; unauthenticated → /login */
function RoleAwareRedirect() {
  const { loading } = useAuth()
  const { role } = useRole()
  if (loading) return <div className="flex h-full items-center justify-center"><Spinner size="lg" /></div>
  return <Navigate to={getDefaultRouteForRole(role || "student")} replace />
}

// ── Auth pages ────────────────────────────────────────────────────────────────
const LoginPage        = lazy(() => import('@/features/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const RegisterPage     = lazy(() => import('@/features/auth/RegisterPage').then(m => ({ default: m.RegisterPage })))
const PasswordResetPage = lazy(() => import('@/features/auth/PasswordResetPage').then(m => ({ default: m.PasswordResetPage })))

// ── Invitation pages ──────────────────────────────────────────────────────────
const AcceptInvitationPage = lazy(() => import('@/features/invitations/AcceptInvitationPage').then(m => ({ default: m.AcceptInvitationPage })))
const InvitationsPage      = lazy(() => import('@/features/invitations/InvitationsPage').then(m => ({ default: m.InvitationsPage })))

// ── Admin pages ───────────────────────────────────────────────────────────────
const AdminDashboardPage  = lazy(() => import('@/features/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })))
const AdminUsersPage      = lazy(() => import('@/features/admin/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })))
const AdminInstitutesPage = lazy(() => import('@/features/admin/AdminInstitutesPage').then(m => ({ default: m.AdminInstitutesPage })))
const AdminUnitsPage      = lazy(() => import('@/features/admin/AdminUnitsPage').then(m => ({ default: m.AdminUnitsPage })))
const AdminAnalyticsPage  = lazy(() => import('@/features/admin/AdminAnalyticsPage').then(m => ({ default: m.AdminAnalyticsPage })))
const AdminTagsPage       = lazy(() => import('@/features/admin/AdminTagsPage').then(m => ({ default: m.AdminTagsPage })))
const AdminTestsPage      = lazy(() => import('@/features/admin/AdminTestsPage').then(m => ({ default: m.AdminTestsPage })))
const UserProfilePage     = lazy(() => import('@/features/admin/UserProfilePage').then(m => ({ default: m.UserProfilePage })))
const AdminImportPage     = lazy(() => import('@/features/import/AdminImportPage').then(m => ({ default: m.AdminImportPage })))
// AdminContentPage removed — Questions and Answers have their own dedicated routes

// ── Institute pages ───────────────────────────────────────────────────────────
const InstituteListPage   = lazy(() => import('@/features/institutes/InstituteListPage').then(m => ({ default: m.InstituteListPage })))
const InstituteDetailPage = lazy(() => import('@/features/institutes/InstituteDetailPage').then(m => ({ default: m.InstituteDetailPage })))
const InstituteFormPage   = lazy(() => import('@/features/institutes/InstituteFormPage').then(m => ({ default: m.InstituteFormPage })))

// ── Unit pages ────────────────────────────────────────────────────────────────
const UnitListPage          = lazy(() => import('@/features/units/UnitListPage').then(m => ({ default: m.UnitListPage })))
const UnitDetailPage        = lazy(() => import('@/features/units/UnitDetailPage').then(m => ({ default: m.UnitDetailPage })))
const UnitFormPage          = lazy(() => import('@/features/units/UnitFormPage').then(m => ({ default: m.UnitFormPage })))
const UnitModuleDetailPage  = lazy(() => import('@/features/units/UnitModuleDetailPage').then(m => ({ default: m.UnitModuleDetailPage })))

// ── Question pages ────────────────────────────────────────────────────────────
const QuestionListPage   = lazy(() => import('@/features/questions/QuestionListPage').then(m => ({ default: m.QuestionListPage })))
const QuestionDetailPage = lazy(() => import('@/features/questions/QuestionDetailPage').then(m => ({ default: m.QuestionDetailPage })))
const QuestionFormPage   = lazy(() => import('@/features/questions/QuestionFormPage').then(m => ({ default: m.QuestionFormPage })))
const SpreadsheetImportPage = lazy(() => import('@/features/import/SpreadsheetImportPage/index').then(m => ({ default: m.SpreadsheetImportPage })))

// ── Answer pages ──────────────────────────────────────────────────────────────
const AnswerListPage             = lazy(() => import('@/features/answers/AnswerListPage').then(m => ({ default: m.AnswerListPage })))
const AnswerFormPage             = lazy(() => import('@/features/answers/AnswerFormPage').then(m => ({ default: m.AnswerFormPage })))
// AssociatedAnswerFormPage removed in v4.0 — answer options are managed via
// question.answerOptions on QuestionDetailPage

// ── Test pages ────────────────────────────────────────────────────────────────
const TestListPage   = lazy(() => import('@/features/tests/TestListPage').then(m => ({ default: m.TestListPage })))
const TestDetailPage = lazy(() => import('@/features/tests/TestDetailPage').then(m => ({ default: m.TestDetailPage })))
const TestFormPage   = lazy(() => import('@/features/tests/TestFormPage').then(m => ({ default: m.TestFormPage })))
const TestRunPage    = lazy(() => import('@/features/tests/TestRunPage').then(m => ({ default: m.TestRunPage })))
const TestRunnerPage = lazy(() => import('@/features/tests/TestRunnerPage').then(m => ({ default: m.TestRunnerPage })))
const TestRunReportPage = lazy(() => import('@/features/tests/TestRunReportPage').then(m => ({ default: m.TestRunReportPage })))
const SectionFormPage = lazy(() => import('@/features/tests/SectionFormPage').then(m => ({ default: m.SectionFormPage })))

// ── Class pages ───────────────────────────────────────────────────────────────
const ClassListPage         = lazy(() => import('@/features/classes/ClassListPage').then(m => ({ default: m.ClassListPage })))
const ClassDetailPage       = lazy(() => import('@/features/classes/ClassDetailPage').then(m => ({ default: m.ClassDetailPage })))
const ClassFormPage         = lazy(() => import('@/features/classes/ClassFormPage').then(m => ({ default: m.ClassFormPage })))
const ClassModuleDetailPage = lazy(() => import('@/features/classes/ClassModuleDetailPage').then(m => ({ default: m.ClassModuleDetailPage })))
const ClassTestResultsPage  = lazy(() => import('@/features/classes/ClassTestResultsPage').then(m => ({ default: m.ClassTestResultsPage })))

// ── Tag pages ─────────────────────────────────────────────────────────────────
const TagListPage = lazy(() => import('@/features/tags/TagListPage').then(m => ({ default: m.TagListPage })))

// ── Student pages ─────────────────────────────────────────────────────────────
const StudentDashboardPage  = lazy(() => import('@/features/student/StudentDashboardPage').then(m => ({ default: m.StudentDashboardPage })))
const StudentEnrollmentPage = lazy(() => import('@/features/student/StudentEnrollmentPage').then(m => ({ default: m.StudentEnrollmentPage })))
const StudentHistoryPage    = lazy(() => import('@/features/student/StudentHistoryPage').then(m => ({ default: m.StudentHistoryPage })))
const ExamPage              = lazy(() => import('@/features/student/ExamPage').then(m => ({ default: m.ExamPage })))
const ResultsPage           = lazy(() => import('@/features/student/ResultsPage').then(m => ({ default: m.ResultsPage })))
const PracticePage          = lazy(() => import('@/features/student/PracticePage').then(m => ({ default: m.PracticePage })))

// ── Error pages ───────────────────────────────────────────────────────────────
const NotFoundPage     = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const UnauthorizedPage = lazy(() => import('@/pages/UnauthorizedPage').then(m => ({ default: m.UnauthorizedPage })))

const Load = () => (
  <div className="flex h-full items-center justify-center">
    <Spinner size="lg" />
  </div>
)

const router = createBrowserRouter([
  // ── Public auth routes ──────────────────────────────────────────────────────
  { path: ROUTES.AUTH.LOGIN,          element: <Suspense fallback={<Load />}><LoginPage /></Suspense> },
  { path: ROUTES.AUTH.REGISTER,       element: <Suspense fallback={<Load />}><RegisterPage /></Suspense> },
  { path: ROUTES.AUTH.RESET_PASSWORD, element: <Suspense fallback={<Load />}><PasswordResetPage /></Suspense> },
  { path: ROUTES.UNAUTHORIZED,        element: <Suspense fallback={<Load />}><UnauthorizedPage /></Suspense> },
  // Public invite acceptance page (no auth required)
  { path: ROUTES.INVITE.ACCEPT,       element: <Suspense fallback={<Load />}><AcceptInvitationPage /></Suspense> },

  // ── Protected layout routes ─────────────────────────────────────────────────
  {
    element: <AppLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Redirect root → role home (if logged in) or login
      { index: true, element: <RoleAwareRedirect /> },

      // Admin
      {
        path: ROUTES.ADMIN.DASHBOARD,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><AdminDashboardPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ADMIN.USERS,
        element: <RequireRole roles={['superuser']}><Suspense fallback={<Load />}><AdminUsersPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ADMIN.USER_PROFILE,
        element: <RequireRole roles={['superuser', 'tutor']}><Suspense fallback={<Load />}><UserProfilePage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ADMIN.INSTITUTES,
        element: <RequireRole roles={['superuser']}><Suspense fallback={<Load />}><AdminInstitutesPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ADMIN.UNITS,
        element: <RequireRole roles={['superuser']}><Suspense fallback={<Load />}><AdminUnitsPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ADMIN.ANALYTICS,
        element: <RequireRole roles={['superuser']}><Suspense fallback={<Load />}><AdminAnalyticsPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ADMIN.TAGS,
        element: <RequireRole roles={['superuser']}><Suspense fallback={<Load />}><AdminTagsPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ADMIN.TESTS,
        element: <RequireRole roles={['superuser']}><Suspense fallback={<Load />}><AdminTestsPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ADMIN.INVITATIONS,
        element: <RequireRole roles={['superuser']}><Suspense fallback={<Load />}><InvitationsPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ADMIN.IMPORT,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><AdminImportPage /></Suspense></RequireRole>,
      },

      // Institutes (superuser + manager + tutor)
      {
        path: ROUTES.INSTITUTE.LIST,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><InstituteListPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.INSTITUTE.DETAIL,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><InstituteDetailPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.INSTITUTE.CREATE,
        element: <RequireRole roles={['superuser', 'manager']}><Suspense fallback={<Load />}><InstituteFormPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.INSTITUTE.EDIT,
        element: <RequireRole roles={['superuser', 'manager']}><Suspense fallback={<Load />}><InstituteFormPage /></Suspense></RequireRole>,
      },

      // Units (superuser + manager + tutor)
      {
        path: ROUTES.UNIT.LIST,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><UnitListPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.UNIT.DETAIL,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><UnitDetailPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.UNIT.MODULE_DETAIL,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><UnitModuleDetailPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.UNIT.CREATE,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><UnitFormPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.UNIT.EDIT,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><UnitFormPage /></Suspense></RequireRole>,
      },

      // Questions (superuser + manager + tutor)
      {
        path: ROUTES.QUESTIONS.LIST,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><QuestionListPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.QUESTIONS.IMPORT,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><SpreadsheetImportPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.QUESTIONS.DETAIL,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><QuestionDetailPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.QUESTIONS.CREATE,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><QuestionFormPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.QUESTIONS.EDIT,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><QuestionFormPage /></Suspense></RequireRole>,
      },

      // Answers (superuser + manager + tutor)
      {
        path: ROUTES.ANSWERS.LIST,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><AnswerListPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ANSWERS.CREATE,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><AnswerFormPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.ANSWERS.EDIT,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><AnswerFormPage /></Suspense></RequireRole>,
      },

      // Associated Answers routes removed in v4.0 — managed via QuestionDetailPage

      // Tests (superuser + manager + tutor)
      {
        path: ROUTES.TESTS.LIST,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><TestListPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.TESTS.DETAIL,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><TestDetailPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.TESTS.CREATE,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><TestFormPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.TESTS.EDIT,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><TestFormPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.TESTS.RUN,
        element: <RequireRole roles={['superuser']}><Suspense fallback={<Load />}><TestRunPage /></Suspense></RequireRole>,
      },
      // v3.0 — Unified runner (all roles; mode controls data tracking)
      {
        path: ROUTES.TESTS.RUNNER,
        element: <RequireRole roles={['superuser', 'manager', 'tutor', 'student']}><Suspense fallback={<Load />}><TestRunnerPage /></Suspense></RequireRole>,
      },
      // v3.0 — Run report (all roles)
      {
        path: ROUTES.TEST_RUN.REPORT,
        element: <RequireRole roles={['superuser', 'manager', 'tutor', 'student']}><Suspense fallback={<Load />}><TestRunReportPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.SECTIONS.CREATE,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><SectionFormPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.SECTIONS.EDIT,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><SectionFormPage /></Suspense></RequireRole>,
      },

      // Classes (superuser + manager + tutor)
      {
        path: ROUTES.CLASSES.LIST,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><ClassListPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.CLASSES.DETAIL,
        element: <RequireRole roles={['superuser', 'manager', 'tutor', 'student']}><Suspense fallback={<Load />}><ClassDetailPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.CLASSES.MODULE_DETAIL,
        element: <RequireRole roles={['superuser', 'manager', 'tutor', 'student']}><Suspense fallback={<Load />}><ClassModuleDetailPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.CLASSES.TEST_RESULTS,
        element: <RequireRole roles={['superuser', 'manager', 'tutor', 'student']}><Suspense fallback={<Load />}><ClassTestResultsPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.CLASSES.CREATE,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><ClassFormPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.CLASSES.EDIT,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><ClassFormPage /></Suspense></RequireRole>,
      },

      // Tags (superuser + manager + tutor)
      {
        path: ROUTES.TAGS.LIST,
        element: <RequireRole roles={['superuser', 'manager', 'tutor']}><Suspense fallback={<Load />}><TagListPage /></Suspense></RequireRole>,
      },
      {
        path: '/student/enrollment',
        element: <Navigate to="/student/classes" replace />,
      },
      {
        path: ROUTES.STUDENT.DASHBOARD,
        element: <RequireRole roles={['superuser', 'manager', 'tutor', 'student']}><Suspense fallback={<Load />}><StudentDashboardPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.STUDENT.ENROLLMENT,
        element: <RequireRole roles={['student']}><Suspense fallback={<Load />}><StudentEnrollmentPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.STUDENT.HISTORY,
        element: <RequireRole roles={['student']}><Suspense fallback={<Load />}><StudentHistoryPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.STUDENT.EXAM,
        element: <RequireRole roles={['student']}><Suspense fallback={<Load />}><ExamPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.STUDENT.RESULTS,
        element: <RequireRole roles={['student']}><Suspense fallback={<Load />}><ResultsPage /></Suspense></RequireRole>,
      },
      {
        path: ROUTES.STUDENT.PRACTICE,
        element: <RequireRole roles={['student']}><Suspense fallback={<Load />}><PracticePage /></Suspense></RequireRole>,
      },
    ],
  },

  // ── 404 ─────────────────────────────────────────────────────────────────────
  { path: '*', element: <Suspense fallback={<Load />}><NotFoundPage /></Suspense> },
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  } as any,
})

/**
 * Application router built with `createBrowserRouter` (React Router 6).
 *
 * Defines the full route tree: public auth pages, invitation flow,
 * role-gated admin/tutor/student routes with lazy-loaded page components,
 * and a catch-all `NotFoundPage`. `RequireRole` guards enforce permission
 * per route group.
 */
export function Router() {
  return <RouterProvider router={router} />
}

